import { ConditionType } from '@bringg/react-components';

import { DISPLAY_PATH_SEPARATOR } from './mapper';
import { IncompatibilityRule, MappedAttribute } from './types';

export const formatAttributeToLowerCase = (attributePath): string => {
	if (!attributePath) {
		return '';
	}
	return attributePath
		.split(DISPLAY_PATH_SEPARATOR)
		.map(attr => (attr.toUpperCase() === attr ? attr : attr.toLowerCase()))
		.join(DISPLAY_PATH_SEPARATOR);
};

const mappedSupportedTypes = new Set([
	ConditionType.LIST,
	ConditionType.BOOLEAN,
	ConditionType.NUMBER,
	ConditionType.STRING,
	ConditionType.ARRAY,
	ConditionType.STRING_ARRAY,
	ConditionType.NUMBER_ARRAY,
	ConditionType.BOOLEAN_ARRAY
]);

const isAttributeSupported = (attribute: MappedAttribute): boolean => {
	return mappedSupportedTypes.has(attribute.type) && (!attribute.options || attribute.options.length > 0);
};

const isAttributeComparable = (attribute: MappedAttribute, getComparableList): boolean =>
	getComparableList(attribute.path)?.length > 0;

const filterAttributes = (attributes: MappedAttribute[], predicate): MappedAttribute[] => {
	const getNestedAttributes = (result, attribute) => {
		if (predicate(attribute)) {
			result.push(attribute);
			return result;
		}
		if (attribute.items) {
			const attrs = attribute.items.reduce(getNestedAttributes, []);
			if (attrs.length) {
				result.push({ ...attribute, items: attrs });
			}
		}
		return result;
	};
	return attributes.reduce(getNestedAttributes, []);
};

export const filterUnsupportedAttributes = (
	attributes: MappedAttribute[],
	getComparableList: (path: string) => string[]
): MappedAttribute[] =>
	filterAttributes(
		attributes,
		attribute => isAttributeSupported(attribute) || isAttributeComparable(attribute, getComparableList)
	);

export const filterIncomparableAttributes = (attributes: MappedAttribute[], comparable: string[]): MappedAttribute[] =>
	filterAttributes(attributes, attribute => comparable.includes(attribute.path));

// Trigger selection context the filter is evaluated against.
// id: the trigger's wire-side type string (e.g. Fleet_Rejected_Task).
// params: a flat record of the trigger's currently-configured param values
// used to evaluate optional when-clauses on incompatibility rules.
export type TriggerContext = {
	id: string;
	params: Record<string, unknown>;
};

/** True iff the current trigger's param value satisfies the `when.values` list. */
const paramMatches = (triggerParamValue: unknown, whenValues: string[]): boolean => {
	if (triggerParamValue === null || triggerParamValue === undefined) return false;
	const triggerValues = Array.isArray(triggerParamValue) ? triggerParamValue : [triggerParamValue];
	return triggerValues.some(v => whenValues.includes(String(v)));
};

/**
 * True iff this single rule matches the current trigger context.
 *
 * TODO BRNGG-54867: the BE wire shape (`IncompatibilityRule`) also exposes
 * `actions` and `attributes` host arrays, but the FE picker context only
 * supplies a trigger today, so only `rule.triggers` is consumed. Extend here
 * (and in `currentTriggerContext` upstream) when an action- or
 * attribute-scoped picker needs to honor the same incompatibility rules.
 */
const ruleMatches = (rule: IncompatibilityRule, context: TriggerContext): boolean => {
	if (!rule.triggers?.includes(context.id)) return false;
	if (!rule.when) return true;
	if (!(rule.when.param in context.params)) {
		// Dev guard: BE declared a when-clause on a param the FE picker context
		// does not forward (today only `value` is forwarded — see
		// `condition-base-store.ts#currentTriggerContext`). Rule silently
		// no-ops. This call site is already FF-gated by the caller, so the
		// warning only fires for tenants on `hide_fleet_id_on_carrier_rejection`.
		// eslint-disable-next-line no-console
		console.warn(
			`[automation-workflows] incompatibility rule references trigger param "${
				rule.when.param
			}" which is not in the FE context (params: ${
				Object.keys(context.params).join(', ') || '<empty>'
			}). Rule treated as non-matching.`
		);
		return false;
	}
	return paramMatches(context.params[rule.when.param], rule.when.values);
};

/** True iff the attribute should remain selectable given the current trigger context. */
const isAttributeAllowed = (attribute: MappedAttribute, context: TriggerContext | undefined): boolean =>
	!context || !attribute.incompatible_with?.some(rule => ruleMatches(rule, context));

/**
 * Recursively filter out attributes whose `incompatible_with` rules match the
 * current trigger context. An attribute is hidden when ANY of its rules
 * matches (rules are OR'd). If `triggerContext` is undefined (e.g. the user
 * hasn't picked a trigger yet) nothing is filtered.
 */
export const filterIncompatibleAttributes = (
	attributes: MappedAttribute[],
	triggerContext: TriggerContext | undefined
): MappedAttribute[] => {
	if (!triggerContext) return attributes;
	return attributes
		.filter(attribute => isAttributeAllowed(attribute, triggerContext))
		.map(attribute =>
			attribute.items
				? { ...attribute, items: filterIncompatibleAttributes(attribute.items, triggerContext) }
				: attribute
		);
};
